/**
 * Collapsible log panel: toolbar + log viewer.
 * Manages internal display state and search logic.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import LogToolbar from './LogToolbar';
import LogViewer from './LogViewer';
import type { HighlightMatch } from './LogViewer';

interface LogPanelProps {
  logs: string[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  taskId?: number;
}

// Match both ISO 8601 (2026-07-09T12:34:56.789+08:00) and
// Beijing-time format (2026-07-09 12:34:56,789) used by BVP backend
const TIMESTAMP_RE = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}[.,]\d+(?:[+-]\d{2}:\d{2})?\s*/g;

const LogPanel: React.FC<LogPanelProps> = ({ logs, isCollapsed, onToggleCollapse, taskId }) => {
  // Display options
  const [wrapText, setWrapText] = useState(false);
  const [hideTimestamp, setHideTimestamp] = useState(false);
  const [darkTheme, setDarkTheme] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Search state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Compute search matches
  const matchResults = useMemo<HighlightMatch[]>(() => {
    if (!searchKeyword) return [];
    const keyword = searchKeyword.toLowerCase();
    const results: HighlightMatch[] = [];
    logs.forEach((line, lineIndex) => {
      const displayLine = hideTimestamp ? line.replace(TIMESTAMP_RE, '') : line;
      const lower = displayLine.toLowerCase();
      let pos = 0;
      while (pos < lower.length) {
        const idx = lower.indexOf(keyword, pos);
        if (idx === -1) break;
        results.push({ lineIndex, startOffset: idx, length: keyword.length });
        pos = idx + 1;
      }
    });
    return results;
  }, [logs, searchKeyword, hideTimestamp]);

  // Reset current match when matches change
  useEffect(() => {
    if (matchResults.length === 0) {
      setCurrentMatchIndex(0);
    } else if (currentMatchIndex >= matchResults.length) {
      setCurrentMatchIndex(0);
    }
  }, [matchResults.length, currentMatchIndex]);

  const handleSearchNext = useCallback(() => {
    if (matchResults.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchResults.length);
  }, [matchResults.length]);

  const handleSearchPrev = useCallback(() => {
    if (matchResults.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchResults.length) % matchResults.length);
  }, [matchResults.length]);

  // Download handler
  const handleDownload = useCallback(() => {
    if (logs.length === 0) return;
    const content = logs.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const now = new Date();
    const ts = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
      '-',
      String(now.getHours()).padStart(2, '0'),
      String(now.getMinutes()).padStart(2, '0'),
      String(now.getSeconds()).padStart(2, '0'),
    ].join('');
    const filename = `bcr-logs-${taskId || 'unknown'}-${ts}.txt`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs, taskId]);

  // Fullscreen escape key
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const panelStyle: React.CSSProperties = isFullscreen
    ? { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', backgroundColor: darkTheme ? '#1e1e1e' : '#fff' }
    : { display: 'flex', flexDirection: 'column', borderTop: '1px solid #e8e8e8', height: isCollapsed ? 36 : '33vh' };

  return (
    <div style={panelStyle}>
      {/* Collapse label bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          height: 36,
          cursor: 'pointer',
          backgroundColor: darkTheme ? '#252526' : '#f0f0f0',
          borderBottom: isCollapsed ? undefined : '1px solid #e8e8e8',
          userSelect: 'none',
          flexShrink: 0,
        }}
        onClick={onToggleCollapse}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: darkTheme ? '#ccc' : '#333' }}>
          {isCollapsed ? '▶' : '▼'} Logs:
        </span>
      </div>

      {!isCollapsed && (
        <>
          <LogToolbar
            searchKeyword={searchKeyword}
            onSearchChange={setSearchKeyword}
            matchCount={matchResults.length}
            currentMatchIndex={currentMatchIndex}
            onSearchPrev={handleSearchPrev}
            onSearchNext={handleSearchNext}
            wrapText={wrapText}
            onWrapTextChange={setWrapText}
            hideTimestamp={hideTimestamp}
            onHideTimestampChange={setHideTimestamp}
            darkTheme={darkTheme}
            onDarkThemeChange={setDarkTheme}
            onDownload={handleDownload}
            downloadDisabled={logs.length === 0}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((f) => !f)}
          />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <LogViewer
              logs={logs}
              highlightMatches={matchResults}
              currentMatchIndex={searchKeyword ? currentMatchIndex : -1}
              wrapText={wrapText}
              hideTimestamp={hideTimestamp}
              darkTheme={darkTheme}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default LogPanel;
